import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSnackbar from "components/SoftSnackbar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { AuthContext } from "context";
import sbOfficeLicenseService from "services/sbOfficeLicenseService";
import { partnerService } from "services/partnerService";
import ComingSoon from "../components/ComingSoon";

const ADMIN_IDS = [1, 2, 3];

const PLAN_OPTIONS = [
  {
    value: "basic",
    label: "SB Office Basis",
    description: "Standardfunktionen",
    pricePerUser: 45,
  },
  {
    value: "plus",
    label: "SB Office Plus",
    description: "Erweitertes Auftragswesen und zusaetzliche Dokumentfunktionen",
    pricePerUser: 65,
  },
  {
    value: "pro",
    label: "SB Office Pro",
    description: "Enthaelt zusaetzlich das Vertragswesen",
    pricePerUser: 90,
  },
];

const USER_ADDON_FIELDS = [
  { key: "addon_ecm_pro", label: "ECM Pro", pricePerUnit: 15, unitLabel: "User" },
  { key: "addon_sboffice_mobile", label: "SBOffice Mobile", pricePerUnit: 8, unitLabel: "User" },
  { key: "addon_sb_pos", label: "SB POS", pricePerUnit: 35, unitLabel: "POS" },
  { key: "addon_sbtime", label: "SB Time", pricePerUnit: 2, unitLabel: "User" },
];

const GLOBAL_ADDON_FIELDS = [
  {
    key: "global_backup_pro",
    label: "SB Backup Pro",
    priceMonthly: 5,
    description: "Mandantenweite Sicherung fuer SBOffice.",
  },
  {
    key: "global_onlineshop_connector",
    label: "Onlineshop Schnittstelle",
    priceMonthly: 50,
    description: "Shop-Anbindung inklusive Datenabgleich.",
  },
];

const LEGACY_BOOLEAN_FIELDS = ["global_sb_connect"];
const DEFAULT_STAMMDATA_AUTOMATIONS = [];
const SBOFFICE_INSTALLER_DOWNLOAD_URL =
  "https://portal.uds-solutions.de/service_downloads/99325461/sboffice/sb-office_install.exe";

const getDefaultStammdataAutomations = () =>
  DEFAULT_STAMMDATA_AUTOMATIONS.map((entry) => ({ ...entry }));

const getDefaultLicenseMix = () => ({
  basic: 0,
  plus: 0,
  pro: 0,
});

const STATUS_OPTIONS = [
  { value: "active", label: "Aktiv" },
  { value: "pending", label: "In Vorbereitung" },
  { value: "blocked", label: "Gesperrt" },
];

const EMPTY_NEW_TENANT = {
  userid: "",
  tenant_name: "",
  plan: "basic",
  license_users: 1,
  license_mix: {
    basic: 1,
    plus: 0,
    pro: 0,
  },
  addon_ecm_pro: 0,
  addon_sboffice_mobile: 0,
  addon_sbtime: 0,
  addon_sb_pos: 0,
  global_backup_pro: false,
  global_onlineshop_connector: false,
  stammdata_automations: getDefaultStammdataAutomations(),
  status: "active",
};

const createEmptyNewTenant = () => ({
  ...EMPTY_NEW_TENANT,
  license_mix: { ...EMPTY_NEW_TENANT.license_mix },
  stammdata_automations: getDefaultStammdataAutomations(),
});

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const parseNonNegativeInt = (value, fallback = 0) => {
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(0, parsed);
};

const parseBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value > 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on", "ja"].includes(normalized)) return true;
    if (["false", "0", "no", "off", "nein"].includes(normalized)) return false;
  }
  return fallback;
};

const normalizeLicenseStatus = (status) => {
  const normalized = typeof status === "string" ? status.trim().toLowerCase() : "";
  if (normalized === "inactive" || normalized === "blocked") return "blocked";
  if (normalized === "pending") return "pending";
  return "active";
};

const normalizeMandantStatus = (status) => {
  const normalized = typeof status === "string" ? status.trim().toLowerCase() : "";
  return normalized === "blocked" ? "blocked" : "active";
};

const getStatusLabel = (status) => {
  switch (normalizeLicenseStatus(status)) {
    case "pending":
      return "In Vorbereitung";
    case "blocked":
      return "Gesperrt";
    default:
      return "Aktiv";
  }
};

const getStatusColor = (status) => {
  switch (normalizeLicenseStatus(status)) {
    case "pending":
      return "warning";
    case "blocked":
      return "error";
    default:
      return "success";
  }
};

const normalizeLicenseMix = (mix, fallbackPlan = "basic", fallbackUsers = 0) => {
  const normalized = getDefaultLicenseMix();

  if (mix && typeof mix === "object") {
    Object.keys(normalized).forEach((planKey) => {
      normalized[planKey] = parseNonNegativeInt(mix[planKey], 0);
    });
  } else if (fallbackUsers > 0 && normalized[fallbackPlan] !== undefined) {
    normalized[fallbackPlan] = parseNonNegativeInt(fallbackUsers, 0);
  }

  return normalized;
};

const getTotalBookedUsers = (licenseMix) =>
  Object.values(normalizeLicenseMix(licenseMix)).reduce((sum, value) => sum + value, 0);

const getDominantPlan = (licenseMix) => {
  const normalized = normalizeLicenseMix(licenseMix);
  let dominantPlan = "basic";
  let maxValue = -1;

  Object.keys(normalized).forEach((planKey) => {
    if (normalized[planKey] > maxValue) {
      maxValue = normalized[planKey];
      dominantPlan = planKey;
    }
  });

  return dominantPlan;
};

const normalizeAutomations = (automations) => {
  if (!Array.isArray(automations) || automations.length === 0) {
    return getDefaultStammdataAutomations();
  }

  return automations.map((row, index) => ({
    id: typeof row?.id === "string" && row.id.trim() ? row.id : `automation-${index + 1}`,
    name: typeof row?.name === "string" && row.name.trim() ? row.name.trim() : `Automatisierung ${index + 1}`,
    quantity: parseNonNegativeInt(row?.quantity, 0),
    pricePerUnit: parseNonNegativeInt(row?.pricePerUnit, 9),
  }));
};

const normalizeTenant = (tenant) => {
  if (!tenant) return null;

  const licenseMix = normalizeLicenseMix(tenant.license_mix, tenant.plan || "basic", tenant.license_users || 0);
  const totalUsers = getTotalBookedUsers(licenseMix);

  return {
    ...tenant,
    plan: getDominantPlan(licenseMix),
    tenant_name: tenant.tenant_name || "",
    status: normalizeLicenseStatus(tenant.status),
    license_mix: licenseMix,
    license_users: totalUsers,
    addon_ecm_pro: parseNonNegativeInt(tenant.addon_ecm_pro, 0),
    addon_sboffice_mobile: parseNonNegativeInt(tenant.addon_sboffice_mobile, 0),
    addon_sbtime: parseNonNegativeInt(tenant.addon_sbtime, 0),
    addon_sb_pos: parseNonNegativeInt(tenant.addon_sb_pos, 0),
    global_backup_pro: parseBoolean(tenant.global_backup_pro, false),
    global_onlineshop_connector: parseBoolean(tenant.global_onlineshop_connector, false),
    ...LEGACY_BOOLEAN_FIELDS.reduce((out, field) => {
      out[field] = parseBoolean(tenant[field], false);
      return out;
    }, {}),
    stammdata_automations: normalizeAutomations(tenant.stammdata_automations),
    mandants: Array.isArray(tenant.mandants)
      ? tenant.mandants.map((mandant) => ({
          ...mandant,
          status: normalizeMandantStatus(mandant.status),
        }))
      : [],
    sbOfficeUsers: Array.isArray(tenant.sbOfficeUsers) ? tenant.sbOfficeUsers : [],
    usage_summary: tenant.usage_summary || null,
  };
};

const buildUpdatePayload = (tenant, options = {}) => {
  const { includeAutomations = true } = options;
  const licenseMix = normalizeLicenseMix(tenant.license_mix, tenant.plan || "basic", tenant.license_users || 0);
  const payload = {
    plan: getDominantPlan(licenseMix),
    license_mix: licenseMix,
    license_users: getTotalBookedUsers(licenseMix),
    tenant_name: tenant.tenant_name,
    addon_ecm_pro: parseNonNegativeInt(tenant.addon_ecm_pro, 0),
    addon_sboffice_mobile: parseNonNegativeInt(tenant.addon_sboffice_mobile, 0),
    addon_sbtime: parseNonNegativeInt(tenant.addon_sbtime, 0),
    addon_sb_pos: parseNonNegativeInt(tenant.addon_sb_pos, 0),
    global_backup_pro: parseBoolean(tenant.global_backup_pro, false),
    global_onlineshop_connector: parseBoolean(tenant.global_onlineshop_connector, false),
    status: tenant.status,
  };

  if (includeAutomations) {
    payload.stammdata_automations = normalizeAutomations(tenant.stammdata_automations);
  }

  return payload;
};

const calculateEstimatedMonthlyPrice = (tenant) => {
  if (!tenant) return 0;

  const licenseMix = normalizeLicenseMix(tenant.license_mix, tenant.plan || "basic", tenant.license_users || 0);
  let total = 0;

  PLAN_OPTIONS.forEach((plan) => {
    total += parseNonNegativeInt(licenseMix[plan.value], 0) * plan.pricePerUser;
  });

  USER_ADDON_FIELDS.forEach((addon) => {
    total += parseNonNegativeInt(tenant[addon.key], 0) * addon.pricePerUnit;
  });

  GLOBAL_ADDON_FIELDS.forEach((addon) => {
    if (parseBoolean(tenant[addon.key], false)) {
      total += addon.priceMonthly;
    }
  });

  normalizeAutomations(tenant.stammdata_automations).forEach((row) => {
    total += parseNonNegativeInt(row.quantity, 0) * parseNonNegativeInt(row.pricePerUnit, 0);
  });

  return total;
};

const formatCurrency = (value) => `${Number(value || 0).toFixed(2)} EUR`;

const formatDateTime = (value) => {
  if (!value) return "Noch kein Sync";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Noch kein Sync";
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isAdminUser = (user) => Boolean(user?.isAdmin) || ADMIN_IDS.includes(user?.id);
const isManagerUser = (user) => Boolean(user?.isPartner) || isAdminUser(user);

const canManageTenant = (user, tenant) => {
  if (!user || !tenant) return false;
  if (isAdminUser(user)) return true;
  return Boolean(user.isPartner && tenant.partnerId === user.id);
};

const getLatestDate = (current, value) => {
  if (!value) return current;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return current;
  if (!current || date > current) return date;
  return current;
};

const getUsageSummary = (tenant) => {
  const mandants = Array.isArray(tenant?.mandants) ? tenant.mandants : [];
  const users = Array.isArray(tenant?.sbOfficeUsers) ? tenant.sbOfficeUsers : [];
  const activeLicensedUserIds = new Set();
  let lastSeenAt = null;

  users.forEach((row) => {
    lastSeenAt = getLatestDate(lastSeenAt, row.last_seen_at);
  });

  mandants.forEach((mandant) => {
    lastSeenAt = getLatestDate(lastSeenAt, mandant.last_seen_at);
    (mandant.mandantUsers || []).forEach((link) => {
      lastSeenAt = getLatestDate(lastSeenAt, link.last_seen_at);
      const sbUser = link.sbofficeUser || users.find((row) => row.id === link.sboffice_user_id);
      if (parseBoolean(link.is_active, false) && parseBoolean(sbUser?.requires_license, false)) {
        activeLicensedUserIds.add(link.sboffice_user_id);
      }
    });
  });

  return {
    mandant_count: tenant?.usage_summary?.mandant_count ?? mandants.length,
    blocked_mandant_count:
      tenant?.usage_summary?.blocked_mandant_count ??
      mandants.filter((mandant) => normalizeMandantStatus(mandant.status) === "blocked").length,
    synced_user_count: tenant?.usage_summary?.synced_user_count ?? users.length,
    active_licensed_user_count:
      tenant?.usage_summary?.active_licensed_user_count ?? activeLicensedUserIds.size,
    last_seen_at: tenant?.usage_summary?.last_seen_at || (lastSeenAt ? lastSeenAt.toISOString() : null),
  };
};

const getMandantUsers = (mandant) =>
  [...(mandant?.mandantUsers || [])].sort(
    (left, right) => parseNonNegativeInt(left.local_user_id, 0) - parseNonNegativeInt(right.local_user_id, 0)
  );

const formatCustomerOptionLabel = (customer, user) => {
  const displayName =
    customer.company ||
    [customer.Vorname, customer.Nachname].filter(Boolean).join(" ") ||
    customer.username ||
    `Webuser ${customer.id}`;
  const email = customer.email ? ` (${customer.email})` : "";
  const partnerInfo = isAdminUser(user) ? ` - Partner: ${customer.partnerId || "keiner"}` : "";

  return `${displayName}${email}${partnerInfo}`;
};

function SBOfficeServicePage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tenants, setTenants] = useState([]);
  const [partnerCustomers, setPartnerCustomers] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const [newTenantOpen, setNewTenantOpen] = useState(false);
  const [newTenantData, setNewTenantData] = useState(createEmptyNewTenant());
  const [newTenantSaving, setNewTenantSaving] = useState(false);
  const [pendingMandantStatusId, setPendingMandantStatusId] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    color: "info",
    title: "Hinweis",
    content: "",
  });

  const [quantityBuffer, setQuantityBuffer] = useState({});
  const [pendingQuantityChange, setPendingQuantityChange] = useState(null);
  const [quantityAck, setQuantityAck] = useState(false);

  useEffect(() => {
    let active = true;

    const loadData = async () => {
      if (!user) return;

      setLoading(true);
      setError("");

      try {
        if (isManagerUser(user)) {
          const [tenantList, customers] = await Promise.all([
            sbOfficeLicenseService.getTenants(),
            partnerService.getCustomers().catch(() => []),
          ]);

          if (!active) return;

          const normalizedTenants = (tenantList || []).map((entry) => normalizeTenant(entry));
          setTenants(normalizedTenants);
          setPartnerCustomers(customers || []);

          if (tenantId) {
            const detailTenant = normalizeTenant(await sbOfficeLicenseService.getTenant(tenantId));
            if (!active) return;
            setTenant(detailTenant);
            setActiveTab(0);
          } else {
            setTenant(null);
          }
        } else {
          try {
            const myTenant = await sbOfficeLicenseService.getMyTenant();
            if (!active) return;
            setTenant(normalizeTenant(myTenant));
          } catch (innerError) {
            if (innerError?.response?.status === 404) {
              if (!active) return;
              setTenant(null);
            } else {
              throw innerError;
            }
          }
        }
      } catch (loadError) {
        if (!active) return;
        console.error("Fehler beim Laden von SBOffice:", loadError);
        setError(loadError?.response?.data?.message || "SBOffice konnte nicht geladen werden.");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      active = false;
    };
  }, [user, tenantId]);

  const monthlyEstimate = useMemo(() => calculateEstimatedMonthlyPrice(tenant), [tenant]);

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const openSnackbar = (config) => {
    setSnackbar({
      open: true,
      color: config.color || "info",
      title: config.title || "Hinweis",
      content: config.content || "",
    });
  };

  const handleOpenTenant = (id) => {
    navigate(`/services/sboffice/${id}`);
  };

  const handleBackToTenantList = () => {
    navigate("/services/sboffice");
  };

  const handleTenantFieldChange = (field, value) => {
    setTenant((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleTenantLicenseMixChange = (planKey, nextValue) => {
    setTenant((prev) => {
      if (!prev) return prev;
      const nextMix = normalizeLicenseMix(prev.license_mix);
      nextMix[planKey] = parseNonNegativeInt(nextValue, 0);
      return {
        ...prev,
        license_mix: nextMix,
        license_users: getTotalBookedUsers(nextMix),
        plan: getDominantPlan(nextMix),
      };
    });
  };

  const handleGlobalAddonToggle = (addonKey, nextEnabled) => {
    setTenant((prev) => {
      if (!prev) return prev;
      return { ...prev, [addonKey]: nextEnabled };
    });
  };

  const requestGlobalAddonToggle = (addon, nextEnabled) => {
    if (!tenant) return;

    const currentValue = parseBoolean(tenant[addon.key], false) ? 1 : 0;
    const nextValue = nextEnabled ? 1 : 0;

    beginQuantityChange({
      fieldKey: addon.key,
      label: `${addon.label} (global)`,
      currentValue,
      nextValue,
      apply: (confirmedValue) => {
        handleGlobalAddonToggle(addon.key, confirmedValue > 0);
      },
    });
  };

  const beginQuantityChange = ({ fieldKey, label, currentValue, nextValue, apply }) => {
    if (nextValue === currentValue) {
      setQuantityBuffer((prev) => {
        if (!hasOwn(prev, fieldKey)) return prev;
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
      return;
    }

    setPendingQuantityChange({
      fieldKey,
      label,
      currentValue,
      nextValue,
      apply,
    });
    setQuantityAck(false);
  };

  const commitNumericField = ({ fieldKey, label, currentValue, onApply }) => {
    const rawValue = hasOwn(quantityBuffer, fieldKey) ? quantityBuffer[fieldKey] : String(currentValue);
    const nextValue = parseNonNegativeInt(rawValue, currentValue);

    beginQuantityChange({
      fieldKey,
      label,
      currentValue,
      nextValue,
      apply: onApply,
    });
  };

  const cancelQuantityDialog = () => {
    if (pendingQuantityChange?.fieldKey) {
      setQuantityBuffer((prev) => {
        const next = { ...prev };
        delete next[pendingQuantityChange.fieldKey];
        return next;
      });
    }
    setPendingQuantityChange(null);
    setQuantityAck(false);
  };

  const confirmQuantityDialog = () => {
    if (!pendingQuantityChange || !quantityAck) return;

    pendingQuantityChange.apply(pendingQuantityChange.nextValue);
    setQuantityBuffer((prev) => {
      const next = { ...prev };
      delete next[pendingQuantityChange.fieldKey];
      return next;
    });
    setPendingQuantityChange(null);
    setQuantityAck(false);
  };

  const handleAutomationNameChange = (rowIndex, value) => {
    setTenant((prev) => {
      if (!prev) return prev;
      const rows = [...normalizeAutomations(prev.stammdata_automations)];
      rows[rowIndex] = { ...rows[rowIndex], name: value };
      return { ...prev, stammdata_automations: rows };
    });
  };

  const handleAddAutomationRow = () => {
    setTenant((prev) => {
      if (!prev) return prev;
      const rows = normalizeAutomations(prev.stammdata_automations);
      rows.push({
        id: `automation-${Date.now()}`,
        name: "Neue Stammdaten-Automatisierung",
        quantity: 0,
        pricePerUnit: 9,
      });
      return { ...prev, stammdata_automations: rows };
    });
  };

  const handleRemoveAutomationRow = (rowIndex) => {
    setTenant((prev) => {
      if (!prev) return prev;
      const rows = normalizeAutomations(prev.stammdata_automations).filter((_, index) => index !== rowIndex);
      return {
        ...prev,
        stammdata_automations: rows.length ? rows : getDefaultStammdataAutomations(),
      };
    });
  };

  const handleMandantStatusChange = async (mandant, nextStatus) => {
    if (!tenant?.id || !mandant?.id) return;

    setPendingMandantStatusId(mandant.id);
    try {
      const updated = normalizeTenant(
        await sbOfficeLicenseService.updateMandantStatus(tenant.id, mandant.id, nextStatus)
      );
      setTenant(updated);

      if (isManagerUser(user)) {
        setTenants((prev) =>
          prev.map((entry) => (entry.id === updated.id ? { ...entry, ...updated } : entry))
        );
      }

      openSnackbar({
        color: "success",
        title: "Mandant aktualisiert",
        content: nextStatus === "blocked" ? "Der Mandant wurde gesperrt." : "Der Mandant wurde entsperrt.",
      });
    } catch (statusError) {
      console.error("Fehler beim Aktualisieren des Mandantenstatus:", statusError);
      openSnackbar({
        color: "error",
        title: "Fehler",
        content: statusError?.response?.data?.message || "Mandantenstatus konnte nicht aktualisiert werden.",
      });
    } finally {
      setPendingMandantStatusId(null);
    }
  };

  const handleSaveTenant = async () => {
    if (!tenant || !tenant.id) return;
    if (!tenant.tenant_name || !tenant.tenant_name.trim()) {
      openSnackbar({
        color: "error",
        title: "Validierung",
        content: "Bitte einen Lizenznamen angeben.",
      });
      return;
    }

    setIsSaving(true);
    try {
      const includeAutomations = canManageTenant(user, tenant);
      const payload = buildUpdatePayload(tenant, { includeAutomations });
      const updated = normalizeTenant(await sbOfficeLicenseService.updateTenant(tenant.id, payload));
      setTenant(updated);

      if (isManagerUser(user)) {
        setTenants((prev) =>
          prev.map((entry) => (entry.id === updated.id ? { ...entry, ...updated } : entry))
        );
      }

      openSnackbar({
        color: "success",
        title: "Gespeichert",
        content: "SBOffice Lizenz wurde aktualisiert.",
      });
    } catch (saveError) {
      console.error("Fehler beim Speichern der SBOffice Lizenz:", saveError);
      openSnackbar({
        color: "error",
        title: "Fehler",
        content: saveError?.response?.data?.message || "Speichern fehlgeschlagen.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateTenant = async () => {
    if (!newTenantData.userid) {
      openSnackbar({
        color: "error",
        title: "Validierung",
        content: "Bitte einen Endkunden auswaehlen.",
      });
      return;
    }
    if (!newTenantData.tenant_name || !newTenantData.tenant_name.trim()) {
      openSnackbar({
        color: "error",
        title: "Validierung",
        content: "Bitte einen Lizenznamen angeben.",
      });
      return;
    }
    if (getTotalBookedUsers(newTenantData.license_mix) <= 0) {
      openSnackbar({
        color: "error",
        title: "Validierung",
        content: "Mindestens eine SBOffice Lizenz (Basis, Plus oder Pro) muss eingetragen sein.",
      });
      return;
    }

    setNewTenantSaving(true);
    try {
      const payload = {
        ...buildUpdatePayload(newTenantData),
        userid: parseInt(newTenantData.userid, 10),
      };
      const createdTenant = normalizeTenant(await sbOfficeLicenseService.createTenant(payload));
      setNewTenantOpen(false);
      setNewTenantData(createEmptyNewTenant());
      setTenants((prev) => [createdTenant, ...prev]);
      navigate(`/services/sboffice/${createdTenant.id}`);
      openSnackbar({
        color: "success",
        title: "Lizenz erstellt",
        content: "Die neue SBOffice Lizenz wurde angelegt.",
      });
    } catch (createError) {
      console.error("Fehler beim Erstellen der SBOffice Lizenz:", createError);
      openSnackbar({
        color: "error",
        title: "Fehler",
        content: createError?.response?.data?.message || "Lizenz konnte nicht erstellt werden.",
      });
    } finally {
      setNewTenantSaving(false);
    }
  };

  const renderNumberInput = ({ fieldKey, label, value, onApply, helperText, disabled = false }) => (
    <Grid item xs={12} md={6} lg={3} key={fieldKey}>
      <SoftBox mb={1}>
        <SoftTypography variant="caption" fontWeight="bold">
          {label}
        </SoftTypography>
      </SoftBox>
      <SoftInput
        type="number"
        value={hasOwn(quantityBuffer, fieldKey) ? quantityBuffer[fieldKey] : String(value)}
        disabled={disabled}
        onChange={(event) =>
          setQuantityBuffer((prev) => ({ ...prev, [fieldKey]: event.target.value }))
        }
        onBlur={() => {
          if (disabled) return;
          commitNumericField({
            fieldKey,
            label,
            currentValue: value,
            onApply,
          });
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
        inputProps={{ min: 0 }}
      />
      {helperText && (
        <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
          {helperText}
        </SoftTypography>
      )}
    </Grid>
  );

  const renderPartnerList = () => (
    <SoftBox py={3}>
      <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <SoftTypography variant="h4" fontWeight="bold">
          SBOffice Lizenzen
        </SoftTypography>
        <SoftButton
          variant="gradient"
          color="info"
          onClick={() => setNewTenantOpen(true)}
          startIcon={<Icon>add</Icon>}
        >
          Neue Lizenz anlegen
        </SoftButton>
      </SoftBox>

      <Grid container spacing={3}>
        {tenants.map((entry) => {
          const entryMix = normalizeLicenseMix(entry.license_mix, entry.plan, entry.license_users);
          const summary = getUsageSummary(entry);
          return (
            <Grid item xs={12} md={6} lg={4} key={entry.id}>
              <Card>
                <SoftBox p={3}>
                  <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <SoftBox>
                      <SoftTypography variant="h6" fontWeight="bold">
                        {entry.tenant_name}
                      </SoftTypography>
                      <SoftTypography variant="button" color="text">
                        {entry.user?.company || "Endkunde"}
                      </SoftTypography>
                    </SoftBox>
                    <SoftBox display="flex" gap={0.75} flexWrap="wrap" justifyContent="flex-end">
                      <Chip label={getStatusLabel(entry.status)} color={getStatusColor(entry.status)} size="small" />
                      <Chip label={`${getTotalBookedUsers(entryMix)} User`} color="info" size="small" />
                    </SoftBox>
                  </SoftBox>

                  <SoftTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                    Basis: {entryMix.basic} | Plus: {entryMix.plus} | Pro: {entryMix.pro}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                    Mandanten: {summary.mandant_count} | Benutzer: {summary.synced_user_count} | Aktiv lizenziert:{" "}
                    {summary.active_licensed_user_count}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                    Gesperrte Mandanten: {summary.blocked_mandant_count}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                    Letzter Sync: {formatDateTime(summary.last_seen_at)}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" fontWeight="medium" display="block" mb={2}>
                    Monatsbetrag: {formatCurrency(calculateEstimatedMonthlyPrice(entry))}
                  </SoftTypography>

                  <SoftButton
                    variant="outlined"
                    color="info"
                    fullWidth
                    onClick={() => handleOpenTenant(entry.id)}
                  >
                    Lizenz verwalten
                  </SoftButton>
                </SoftBox>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {!tenants.length && (
        <Card>
          <SoftBox p={4} textAlign="center">
            <SoftTypography variant="h6" fontWeight="medium" mb={1}>
              Noch keine SBOffice Lizenzen vorhanden
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              Legen Sie die erste Lizenz fuer einen Ihrer Endkunden an.
            </SoftTypography>
          </SoftBox>
        </Card>
      )}
    </SoftBox>
  );

  const renderAutomationTable = (canEditAutomations) => {
    const rows = normalizeAutomations(tenant.stammdata_automations);

    return (
      <SoftBox p={2} borderRadius="lg" sx={{ border: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
        <SoftBox
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          gap={1}
        >
          <SoftBox>
            <SoftTypography variant="h6" fontWeight="bold">
              Stammdaten-Automatisierungen
            </SoftTypography>
            {!canEditAutomations && (
              <SoftTypography variant="caption" color="text">
                Nur Admins und zustaendige Partner koennen Eintraege bearbeiten.
              </SoftTypography>
            )}
          </SoftBox>
          {canEditAutomations && (
            <SoftButton variant="outlined" color="info" size="small" onClick={handleAddAutomationRow}>
              Zeile hinzufuegen
            </SoftButton>
          )}
        </SoftBox>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Automatisierung</TableCell>
                <TableCell>Anzahl</TableCell>
                <TableCell>Preis / Einheit</TableCell>
                {canEditAutomations && <TableCell align="right">Aktion</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEditAutomations ? 4 : 3}>
                    <SoftTypography variant="caption" color="text">
                      Keine Stammdaten-Automatisierungen vorhanden.
                    </SoftTypography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, rowIndex) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <SoftInput
                        disabled={!canEditAutomations}
                        value={row.name}
                        onChange={(event) => handleAutomationNameChange(rowIndex, event.target.value)}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <SoftInput
                        disabled={!canEditAutomations}
                        type="number"
                        value={
                          hasOwn(quantityBuffer, `stammdata-${row.id}-quantity`)
                            ? quantityBuffer[`stammdata-${row.id}-quantity`]
                            : String(row.quantity)
                        }
                        onChange={(event) =>
                          setQuantityBuffer((prev) => ({
                            ...prev,
                            [`stammdata-${row.id}-quantity`]: event.target.value,
                          }))
                        }
                        onBlur={() => {
                          if (!canEditAutomations) return;
                          commitNumericField({
                            fieldKey: `stammdata-${row.id}-quantity`,
                            label: `${row.name} (Stammdaten-Automatisierung)`,
                            currentValue: row.quantity,
                            onApply: (nextValue) => {
                              setTenant((prev) => {
                                if (!prev) return prev;
                                const nextRows = [...normalizeAutomations(prev.stammdata_automations)];
                                nextRows[rowIndex] = { ...nextRows[rowIndex], quantity: nextValue };
                                return { ...prev, stammdata_automations: nextRows };
                              });
                            },
                          });
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            event.currentTarget.blur();
                          }
                        }}
                        inputProps={{ min: 0 }}
                      />
                    </TableCell>
                    <TableCell>
                      <SoftTypography variant="caption" fontWeight="medium">
                        {formatCurrency(row.pricePerUnit)}
                      </SoftTypography>
                    </TableCell>
                    {canEditAutomations && (
                      <TableCell align="right">
                        <SoftButton
                          variant="text"
                          color="error"
                          size="small"
                          onClick={() => handleRemoveAutomationRow(rowIndex)}
                        >
                          Entfernen
                        </SoftButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SoftBox>
    );
  };

  const renderMandantsAndUsers = (summary) => {
    const mandants = Array.isArray(tenant.mandants) ? tenant.mandants : [];
    const canEditMandants = canManageTenant(user, tenant);

    return (
      <Grid container spacing={3}>
        {[
          { label: "Mandanten", value: summary.mandant_count },
          { label: "Gesperrt", value: summary.blocked_mandant_count },
          { label: "Synchronisierte Benutzer", value: summary.synced_user_count },
          { label: "Aktiv lizenzpflichtig", value: summary.active_licensed_user_count },
          { label: "Letzter Sync", value: formatDateTime(summary.last_seen_at) },
        ].map((item) => (
          <Grid item xs={12} sm={6} lg={3} key={item.label}>
            <SoftBox p={2} borderRadius="lg" sx={{ border: "1px solid #e0e0e0", minHeight: 86 }}>
              <SoftTypography variant="caption" color="text" fontWeight="medium">
                {item.label}
              </SoftTypography>
              <SoftTypography variant="h6" fontWeight="bold">
                {item.value}
              </SoftTypography>
            </SoftBox>
          </Grid>
        ))}

        <Grid item xs={12}>
          {mandants.length === 0 ? (
            <SoftBox p={3} borderRadius="lg" sx={{ border: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
              <SoftTypography variant="body2" color="text">
                Es wurden noch keine SBOffice-Mandanten synchronisiert.
              </SoftTypography>
            </SoftBox>
          ) : (
            mandants.map((mandant) => {
              const mandantUsers = getMandantUsers(mandant);
              const mandantBlocked = normalizeMandantStatus(mandant.status) === "blocked";
              const nextMandantStatus = mandantBlocked ? "active" : "blocked";
              const statusActionDisabled = pendingMandantStatusId === mandant.id;
              return (
                <SoftBox
                  key={mandant.id}
                  mb={2}
                  p={2}
                  borderRadius="lg"
                  sx={{ border: "1px solid #e0e0e0", backgroundColor: "#fff" }}
                >
                  <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={2}>
                    <SoftBox>
                      <SoftTypography variant="h6" fontWeight="bold">
                        {mandant.firma_name || "SBOffice Mandant"}
                      </SoftTypography>
                      <SoftTypography variant="caption" color="text" display="block">
                        Installation-ID: {mandant.installation_id || "nicht hinterlegt"}
                      </SoftTypography>
                    </SoftBox>
                    <SoftBox display="flex" gap={0.75} flexWrap="wrap" justifyContent="flex-end">
                      <Chip
                        label={mandantBlocked ? "Gesperrt" : "Aktiv"}
                        color={mandantBlocked ? "error" : "success"}
                        size="small"
                      />
                      <Chip label={formatDateTime(mandant.last_seen_at)} color="default" size="small" />
                      {canEditMandants && (
                        <SoftButton
                          variant="outlined"
                          color={mandantBlocked ? "success" : "error"}
                          size="small"
                          disabled={statusActionDisabled}
                          onClick={() => handleMandantStatusChange(mandant, nextMandantStatus)}
                        >
                          {statusActionDisabled
                            ? "Wird gespeichert..."
                            : mandantBlocked
                            ? "Entsperren"
                            : "Sperren"}
                        </SoftButton>
                      )}
                    </SoftBox>
                  </SoftBox>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Lokaler Benutzer</TableCell>
                          <TableCell>Portal-Benutzer</TableCell>
                          <TableCell>Lizenzpflichtig</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Letzter Sync</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mandantUsers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <SoftTypography variant="caption" color="text">
                                Keine Benutzer fuer diesen Mandanten synchronisiert.
                              </SoftTypography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          mandantUsers.map((link) => {
                            const sbUser = link.sbofficeUser || {};
                            const localName = link.local_display_name || `Lokaler User ${link.local_user_id}`;
                            return (
                              <TableRow key={`${link.mandant_id}-${link.sboffice_user_id}`}>
                                <TableCell>
                                  <SoftTypography variant="caption" fontWeight="medium">
                                    {localName}
                                  </SoftTypography>
                                  <SoftTypography variant="caption" color="text" display="block">
                                    Local-ID: {link.local_user_id}
                                  </SoftTypography>
                                </TableCell>
                                <TableCell>
                                  <SoftTypography variant="caption" fontWeight="medium">
                                    {sbUser.display_name || sbUser.username || "Nicht zugeordnet"}
                                  </SoftTypography>
                                  {sbUser.username && (
                                    <SoftTypography variant="caption" color="text" display="block">
                                      {sbUser.username}
                                    </SoftTypography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={parseBoolean(sbUser.requires_license, true) ? "Ja" : "Nein"}
                                    color={parseBoolean(sbUser.requires_license, true) ? "info" : "default"}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={parseBoolean(link.is_active, false) ? "Aktiv" : "Inaktiv"}
                                    color={parseBoolean(link.is_active, false) ? "success" : "default"}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <SoftTypography variant="caption" color="text">
                                    {formatDateTime(link.last_seen_at)}
                                  </SoftTypography>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </SoftBox>
              );
            })
          )}
        </Grid>
      </Grid>
    );
  };

  const renderTenantDetails = () => {
    if (!tenant) return null;

    const canEditTenant = canManageTenant(user, tenant);
    const licenseMix = normalizeLicenseMix(tenant.license_mix, tenant.plan, tenant.license_users);
    const totalBookedUsers = getTotalBookedUsers(licenseMix);
    const summary = getUsageSummary(tenant);

    return (
      <SoftBox py={3}>
        <SoftBox
          mb={2}
          display="flex"
          justifyContent={isManagerUser(user) ? "space-between" : "flex-end"}
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          {isManagerUser(user) && (
            <SoftButton variant="text" color="info" onClick={handleBackToTenantList}>
              <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
              Zurueck zur Lizenzliste
            </SoftButton>
          )}
          <SoftButton
            variant="gradient"
            color="info"
            component="a"
            href={SBOFFICE_INSTALLER_DOWNLOAD_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon sx={{ mr: 0.5 }}>download</Icon>
            Download SBOffice
          </SoftButton>
        </SoftBox>

        <Card sx={{ mb: 3 }}>
          <SoftBox
            p={3}
            sx={{
              background: "linear-gradient(135deg, rgba(107, 0, 0, 0.94) 0%, rgba(33, 4, 4, 0.94) 100%)",
              color: "#fff",
              borderRadius: "0.75rem",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <SoftTypography variant="h4" fontWeight="bold" color="white">
                  {tenant.tenant_name}
                </SoftTypography>
                <SoftTypography variant="button" color="white" opacity={0.85}>
                  Status: {getStatusLabel(tenant.status)}
                </SoftTypography>
                {tenant.user?.company && (
                  <SoftTypography variant="button" display="block" color="white" opacity={0.85}>
                    Endkunde: {tenant.user.company}
                  </SoftTypography>
                )}
                <SoftTypography variant="caption" display="block" color="white" opacity={0.75} mt={1}>
                  Bei laengeren Vertragslaufzeiten sind Preisnachlaesse moeglich. Bitte wenden Sie sich an den
                  Vertrieb.
                </SoftTypography>
              </Grid>
              <Grid item xs={12} md={4}>
                <SoftBox p={2} borderRadius="lg" sx={{ background: "rgba(255,255,255,0.12)" }}>
                  <SoftTypography variant="caption" color="white" opacity={0.9}>
                    Geschaetzte Monatskosten
                  </SoftTypography>
                  <SoftTypography variant="h4" color="white" fontWeight="bold">
                    {formatCurrency(monthlyEstimate)}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="white" opacity={0.75}>
                    Abrechnung basiert auf der hoechsten Anzahl im Monat.
                  </SoftTypography>
                </SoftBox>
              </Grid>
            </Grid>
          </SoftBox>
        </Card>

        <Card>
          <SoftBox p={3}>
            <Tabs value={activeTab} onChange={(_, tabValue) => setActiveTab(tabValue)} indicatorColor="primary">
              <Tab label="Allgemein" />
              <Tab label="User-Lizenzen & Add-ons" />
              <Tab label="Mandanten & Benutzer" />
            </Tabs>

            <SoftBox mt={3}>
              {activeTab === 0 && (
                <Grid container spacing={3}>
                  {!canEditTenant && (
                    <Grid item xs={12}>
                      <SoftBox p={2} borderRadius="lg" sx={{ border: "1px solid #e0e0e0", backgroundColor: "#f7f7f7" }}>
                        <SoftTypography variant="body2" color="text">
                          Dieser Bereich ist fuer Ihren Zugang nur lesbar.
                        </SoftTypography>
                      </SoftBox>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <SoftBox mb={1}>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Lizenzname
                      </SoftTypography>
                    </SoftBox>
                    <SoftInput
                      value={tenant.tenant_name}
                      disabled={!canEditTenant}
                      onChange={(event) => handleTenantFieldChange("tenant_name", event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={tenant.status || "active"}
                        label="Status"
                        disabled={!canEditTenant}
                        onChange={(event) => handleTenantFieldChange("status", event.target.value)}
                      >
                        {STATUS_OPTIONS.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            {status.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <SoftBox
                      p={2.5}
                      borderRadius="lg"
                      sx={{
                        border: "1px solid #e3e8ef",
                        background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                      }}
                    >
                      <SoftTypography variant="h6" fontWeight="bold" mb={2}>
                        Globale Mandanten-Add-ons
                      </SoftTypography>
                      <Grid container spacing={2}>
                        {GLOBAL_ADDON_FIELDS.map((addon) => (
                          <Grid item xs={12} md={6} key={addon.key}>
                            <SoftBox
                              p={2}
                              borderRadius="lg"
                              sx={{ border: "1px solid #d8e2ee", minHeight: 118 }}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                            >
                              <SoftBox pr={1.5}>
                                <SoftTypography variant="button" fontWeight="bold" display="block">
                                  {addon.label}
                                </SoftTypography>
                                <SoftTypography variant="caption" color="text" display="block" mt={0.4}>
                                  {addon.description}
                                </SoftTypography>
                                <SoftTypography variant="caption" color="info" fontWeight="medium" display="block" mt={0.6}>
                                  {formatCurrency(addon.priceMonthly)} / Monat
                                </SoftTypography>
                              </SoftBox>
                              <Switch
                                checked={parseBoolean(tenant[addon.key], false)}
                                disabled={!canEditTenant}
                                onChange={(event) => requestGlobalAddonToggle(addon, event.target.checked)}
                                color="info"
                              />
                            </SoftBox>
                          </Grid>
                        ))}
                      </Grid>
                    </SoftBox>
                  </Grid>

                  <Grid item xs={12}>
                    {renderAutomationTable(canEditTenant)}
                  </Grid>
                </Grid>
              )}

              {activeTab === 1 && (
                <Grid container spacing={3}>
                  {PLAN_OPTIONS.map((planOption) =>
                    renderNumberInput({
                      fieldKey: `license_mix-${planOption.value}`,
                      label: planOption.label,
                      value: licenseMix[planOption.value],
                      disabled: !canEditTenant,
                      helperText: `${formatCurrency(planOption.pricePerUser)} pro User / Monat`,
                      onApply: (nextValue) => {
                        handleTenantLicenseMixChange(planOption.value, nextValue);
                      },
                    })
                  )}

                  <Grid item xs={12}>
                    <SoftBox p={2} borderRadius="lg" sx={{ border: "1px solid #e0e0e0", backgroundColor: "#f7f7f7" }}>
                      <SoftTypography variant="body2" color="text">
                        Gesamtanzahl User-Lizenzen: <strong>{totalBookedUsers}</strong>
                      </SoftTypography>
                    </SoftBox>
                  </Grid>

                  {USER_ADDON_FIELDS.map((addon) =>
                    renderNumberInput({
                      fieldKey: addon.key,
                      label: addon.label,
                      value: tenant[addon.key],
                      disabled: !canEditTenant,
                      helperText: `${formatCurrency(addon.pricePerUnit)} pro ${addon.unitLabel} / Monat`,
                      onApply: (nextValue) => {
                        handleTenantFieldChange(addon.key, nextValue);
                      },
                    })
                  )}

                  <Grid item xs={12}>
                    <SoftBox
                      p={2}
                      borderRadius="lg"
                      sx={{ border: "1px solid #e0e0e0", backgroundColor: "#fff8f2" }}
                    >
                      <SoftTypography variant="body2" color="text">
                        Mengenanpassungen sind kostenrelevant. Bei Senkungen kann es zu Ausfaellen kommen, wenn zu
                        wenig Lizenzen bereitstehen. Es wird die hoechste Anzahl pro Monat pauschal berechnet.
                      </SoftTypography>
                      <SoftTypography variant="body2" color="text" mt={1}>
                        Bei laengeren Vertragslaufzeiten sind Preisnachlaesse moeglich. Bitte wenden Sie sich an den
                        Vertrieb.
                      </SoftTypography>
                    </SoftBox>
                  </Grid>
                </Grid>
              )}

              {activeTab === 2 && renderMandantsAndUsers(summary)}
            </SoftBox>

            {canEditTenant && (
              <SoftBox mt={4} display="flex" justifyContent="flex-end">
                <SoftButton
                  variant="gradient"
                  color="info"
                  onClick={handleSaveTenant}
                  disabled={isSaving}
                >
                  {isSaving ? "Speichern..." : "Aenderungen speichern"}
                </SoftButton>
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </SoftBox>
    );
  };

  const renderCustomerFallback = () => (
    <ComingSoon
      title="SBOffice"
      description="SBOffice ist fuer Ihren Zugang aktuell noch nicht freigeschaltet. Sobald Ihre Lizenz bereitsteht, sehen Sie hier direkt Ihre Lizenzdetails."
      productUrl="https://www.unitas.cloud"
    />
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {loading ? (
          <SoftBox minHeight="50vh" display="flex" justifyContent="center" alignItems="center">
            <CircularProgress color="info" />
          </SoftBox>
        ) : error ? (
          <Card>
            <SoftBox p={3} textAlign="center">
              <SoftTypography variant="h5" color="error">
                {error}
              </SoftTypography>
            </SoftBox>
          </Card>
        ) : isManagerUser(user) ? (
          tenantId ? renderTenantDetails() : renderPartnerList()
        ) : tenant ? (
          renderTenantDetails()
        ) : (
          renderCustomerFallback()
        )}
      </SoftBox>

      <Dialog open={newTenantOpen} onClose={() => setNewTenantOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neue SBOffice Lizenz anlegen</DialogTitle>
        <DialogContent>
          <SoftBox mt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Endkunde</InputLabel>
                  <Select
                    value={newTenantData.userid}
                    label="Endkunde"
                    onChange={(event) =>
                      setNewTenantData((prev) => ({ ...prev, userid: event.target.value }))
                    }
                  >
                    {partnerCustomers.map((customer) => (
                      <MenuItem key={customer.id} value={customer.id}>
                        {formatCustomerOptionLabel(customer, user)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <SoftInput
                  placeholder="Lizenzname"
                  value={newTenantData.tenant_name}
                  onChange={(event) =>
                    setNewTenantData((prev) => ({ ...prev, tenant_name: event.target.value }))
                  }
                />
              </Grid>
              {PLAN_OPTIONS.map((planOption) => (
                <Grid item xs={12} md={4} key={`new-${planOption.value}`}>
                  <SoftBox mb={1}>
                    <SoftTypography variant="caption" fontWeight="bold">
                      {planOption.label}
                    </SoftTypography>
                  </SoftBox>
                  <SoftInput
                    type="number"
                    value={normalizeLicenseMix(newTenantData.license_mix)[planOption.value]}
                    onChange={(event) =>
                      setNewTenantData((prev) => {
                        const nextMix = normalizeLicenseMix(prev.license_mix);
                        nextMix[planOption.value] = parseNonNegativeInt(event.target.value, 0);
                        return {
                          ...prev,
                          license_mix: nextMix,
                          license_users: getTotalBookedUsers(nextMix),
                          plan: getDominantPlan(nextMix),
                        };
                      })
                    }
                    inputProps={{ min: 0 }}
                  />
                  <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                    {formatCurrency(planOption.pricePerUser)} pro User / Monat
                  </SoftTypography>
                </Grid>
              ))}
              <Grid item xs={12}>
                <SoftTypography variant="caption" color="text">
                  Gebuchte User gesamt: {getTotalBookedUsers(newTenantData.license_mix)}
                </SoftTypography>
              </Grid>
            </Grid>
          </SoftBox>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setNewTenantOpen(false)}>
            Abbrechen
          </SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleCreateTenant} disabled={newTenantSaving}>
            {newTenantSaving ? "Wird angelegt..." : "Lizenz anlegen"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingQuantityChange)}
        onClose={cancelQuantityDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mengenaenderung bestaetigen</DialogTitle>
        <DialogContent>
          {pendingQuantityChange && (
            <SoftBox>
              <SoftTypography variant="body2" color="text" mb={2}>
                Feld: <strong>{pendingQuantityChange.label}</strong>
              </SoftTypography>
              <SoftTypography variant="body2" color="text" mb={2}>
                Bisher: {pendingQuantityChange.currentValue} | Neu: {pendingQuantityChange.nextValue}
              </SoftTypography>
              {pendingQuantityChange.nextValue > pendingQuantityChange.currentValue ? (
                <SoftTypography variant="body2" color="text" mb={2}>
                  Durch die Erhoehung entstehen automatisch zusaetzliche Kosten.
                </SoftTypography>
              ) : (
                <SoftTypography variant="body2" color="text" mb={2}>
                  Bei einer Reduktion kann es zu Ausfaellen kommen, wenn zu wenig Lizenzen zur Verfuegung stehen.
                </SoftTypography>
              )}
              <SoftTypography variant="body2" color="text" mb={1}>
                Abrechnungsregel: Die hoechste Anzahl pro Monat wird pauschal berechnet.
              </SoftTypography>
              <FormControlLabel
                control={
                  <Checkbox checked={quantityAck} onChange={(event) => setQuantityAck(event.target.checked)} />
                }
                label="Ich habe den Hinweis gelesen und verstanden."
              />
            </SoftBox>
          )}
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={cancelQuantityDialog}>
            Abbrechen
          </SoftButton>
          <SoftButton variant="gradient" color="info" disabled={!quantityAck} onClick={confirmQuantityDialog}>
            Aenderung uebernehmen
          </SoftButton>
        </DialogActions>
      </Dialog>

      <SoftSnackbar
        color={snackbar.color}
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.open}
        onClose={closeSnackbar}
        close={closeSnackbar}
        icon="notifications"
      />

      <Footer />
    </DashboardLayout>
  );
}

export default SBOfficeServicePage;
